import React, { useState, useEffect, useContext } from 'react';
import { API_URL } from '../utils/api';
import { AuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { 
    IconSearch, 
    IconMapPin, 
    IconCalendar, 
    IconGraduationCap, 
    IconUsers, 
    IconFilter,
    IconChevronRight,
    IconPlus,
    IconBriefcase,
    IconCheck,
    IconTrash,
    IconEdit,
    IconFileText,
    IconLoader,
    LeafIcon
} from '../components/ui/Icons';
import ResumeModal from '../components/career/ResumeModal';
import DirectApplyModal from '../components/career/DirectApplyModal';
import PricingModal from '../components/career/PricingModal';
import PayPalCheckout from '../components/career/PayPalCheckout';

const Career = () => {
    const [jobs, setJobs] = useState([]);
    const [sponsors, setSponsors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('ALL');

    const categories = [
        { id: 'ALL', label: 'Alle' },
        { id: 'APPRENTICE', label: 'Ausbildung' },
        { id: 'INTERN', label: 'Praktikum' },
        { id: 'SCHUELER', label: 'Schülerpraktikum' },
        { id: 'SPECIALIST', label: 'Fachkraft' },
        { id: 'MASTER', label: 'Meister/Techniker' },
        { id: 'ENTREPRENEUR', label: 'Unternehmertum / Nachfolge' },
    ];

    const { user, fetchWithAuth } = useContext(AuthContext);
    const { showToast } = useToast();
    const [userProfile, setUserProfile] = useState(null);
    const [showApplyModal, setShowApplyModal] = useState(false);
    const [showPostJobModal, setShowPostJobModal] = useState(false);
    const [showSponsorModal, setShowSponsorModal] = useState(false);
    const [showDirectApplyModal, setShowDirectApplyModal] = useState(false);
    const [showResumeModal, setShowResumeModal] = useState(false);
    const [selectedJob, setSelectedJob] = useState(null);
    const [mySponsor, setMySponsor] = useState(null);
    const [myResume, setMyResume] = useState(null);
    const [editingJob, setEditingJob] = useState(null);
    const [myApplications, setMyApplications] = useState([]);
    const [myDocuments, setMyDocuments] = useState([]);
    const [applicationStatus, setApplicationStatus] = useState({ loading: false, success: false, error: null });
    const [careerSettings, setCareerSettings] = useState({ career_opt_in: false, career_skills_public: false, career_region: '' });
    const [careerSettingsLoading, setCareerSettingsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('jobs'); // 'jobs' | 'talent' (for employers)
    const [showPricingModal, setShowPricingModal] = useState(false);
    const [pricingLoading, setPricingLoading] = useState(false);
    const [featureJobLoading, setFeatureJobLoading] = useState(null); // job id being featured
    const [paypalJobId, setPaypalJobId] = useState(null); // job id currently showing paypal button

    const fetchData = async () => {
        setLoading(true);
        try {
            const [jobsRes, sponsorsRes] = await Promise.all([
                fetch(`${API_URL}/api/jobs/`),
                fetch(`${API_URL}/api/sponsors/`)
            ]);

            if (jobsRes.ok) setJobs(await jobsRes.json());
            if (sponsorsRes.ok) setSponsors(await sponsorsRes.json());

            if (user) {
                const profileRes = await fetchWithAuth('/api/profile/');
                if (profileRes.ok) {
                    const profileData = await profileRes.json();
                    setUserProfile(profileData);

                    // Fetch resume for candidates
                    if (['AZUBI', 'FACHARBEITER', 'SCHUELER'].includes(profileData.role) || profileData.is_staff) {
                        const [resumeRes, appRes, docRes, settingsRes] = await Promise.all([
                            fetchWithAuth('/api/career/resumes/'),
                            fetchWithAuth('/api/career/applications/'),
                            fetchWithAuth('/api/career/documents/'),
                            fetchWithAuth('/api/career/settings/'),
                        ]);
                        
                        if (resumeRes.ok) {
                            const resumes = await resumeRes.json();
                            if (resumes.length > 0) setMyResume(resumes[0]);
                        }
                        if (appRes.ok) setMyApplications(await appRes.json());
                        if (docRes.ok) setMyDocuments(await docRes.json());
                        if (settingsRes.ok) setCareerSettings(await settingsRes.json());
                    }

                    // If employer, fetch their sponsor profile
                    if (profileData.is_authorized_employer) {
                        const sponsorRes = await fetchWithAuth('/api/sponsors/me/');
                        if (sponsorRes.ok) setMySponsor(await sponsorRes.json());
                    }
                }
            }
        } catch (error) {
            console.error("Error fetching career data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [user]);

    // Handle URL parameters for direct modal opening
    useEffect(() => {
        if (!userProfile) return;
        
        const params = new URLSearchParams(window.location.search);
        const action = params.get('action');
        
        if (action === 'manage_profile' && userProfile.is_authorized_employer) {
            setShowSponsorModal(true);
        } else if (action === 'post_job' && userProfile.is_authorized_employer) {
            setShowPostJobModal(true);
        }
    }, [userProfile]);

    const handleApplyAsEmployer = async (e) => {
        e.preventDefault();
        setApplicationStatus({ loading: true, success: false, error: null });
        const formData = new FormData(e.target);
        const data = Object.fromEntries(formData.entries());

        try {
            const res = await fetchWithAuth('/api/jobs/apply_as_employer/', {
                method: 'POST',
                body: JSON.stringify(data)
            });

            if (res.ok) {
                setApplicationStatus({ loading: false, success: true, error: null });
                // Refresh profile to show "Requested" status
                const profileRes = await fetchWithAuth('/api/profile/');
                if (profileRes.ok) setUserProfile(await profileRes.json());
            } else {
                const err = await res.json();
                setApplicationStatus({ loading: false, success: false, error: err.detail || "Fehler beim Senden des Antrags." });
            }
        } catch (error) {
            setApplicationStatus({ loading: false, success: false, error: "Netzwerkfehler." });
        }
    };

    const handleUpdateSponsor = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const data = Object.fromEntries(formData.entries());

        try {
            const res = await fetchWithAuth('/api/sponsors/me/', {
                method: 'PATCH',
                body: JSON.stringify(data)
            });

            if (res.ok) {
                showToast?.("Profil aktualisiert!", "success");
                setShowSponsorModal(false);
                fetchData();
            } else {
                const err = await res.json();
                alert("Fehler beim Speichern: " + JSON.stringify(err));
            }
        } catch (error) {
            alert("Verbindungsfehler.");
        }
    };

    const handleUpdateResume = async (resumeData) => {
        try {
            const method = myResume?.id ? 'PATCH' : 'POST';
            const url = myResume?.id ? `/api/career/resumes/${myResume.id}/` : '/api/career/resumes/';
            
            const res = await fetchWithAuth(url, {
                method,
                body: JSON.stringify(resumeData)
            });

            if (res.ok) {
                const updatedResume = await res.json();
                setMyResume(updatedResume);
                showToast?.("Lebenslauf gespeichert!", "success");
                setShowResumeModal(false);
                return true;
            }
            return false;
        } catch (err) {
            console.error("error updating resume", err);
            return false;
        }
    };

    const handleDirectApply = async (e) => {
        e.preventDefault();
        setApplicationStatus({ loading: true, success: false, error: null });
        const formData = new FormData(e.target);
        const data = {
            job: selectedJob.id,
            cover_letter: formData.get('cover_letter'),
            document_ids: Array.from(formData.getAll('document_ids')).map(Number)
        };

        try {
            const res = await fetchWithAuth('/api/career/applications/', {
                method: 'POST',
                body: JSON.stringify(data)
            });

            if (res.ok) {
                setApplicationStatus({ loading: false, success: true, error: null });
                showToast?.("Bewerbung erfolgreich gesendet!", "success");
                setTimeout(() => {
                    setShowDirectApplyModal(false);
                    setApplicationStatus({ loading: false, success: false, error: null });
                }, 2000);
            } else {
                const err = await res.json();
                setApplicationStatus({ loading: false, success: false, error: err.detail || "Fehler beim Senden." });
            }
        } catch (error) {
            setApplicationStatus({ loading: false, success: false, error: "Netzwerkfehler." });
        }
    };

    const handleDeleteJob = async (id) => {
        if (!window.confirm("Anzeige wirklich löschen?")) return;
        try {
            const res = await fetchWithAuth(`/api/jobs/${id}/`, { method: 'DELETE' });
            if (res.ok) {
                setJobs(jobs.filter(j => j.id !== id));
                showToast?.("Anzeige gelöscht.", "success");
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleDeleteDocument = async (id) => {
        if (!window.confirm("Dokument wirklich löschen?")) return;
        try {
            const res = await fetchWithAuth(`/api/career/documents/${id}/`, { method: 'DELETE' });
            if (res.ok) {
                setMyDocuments(myDocuments.filter(d => d.id !== id));
                showToast?.("Dokument gelöscht.", "success");
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleUploadDocument = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        
        try {
            const res = await fetchWithAuth('/api/career/documents/', {
                method: 'POST',
                body: formData,
                headers: {} // Let browser set boundary for multipart
            });

            if (res.ok) {
                const newDoc = await res.json();
                setMyDocuments([...myDocuments, newDoc]);
                showToast?.("Dokument hochgeladen!", "success");
                e.target.reset();
            } else {
                alert("Fehler beim Upload.");
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handlePostJob = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const data = Object.fromEntries(formData.entries());

        try {
            const method = editingJob ? 'PATCH' : 'POST';
            const url = editingJob ? `/api/jobs/${editingJob.id}/` : '/api/jobs/';

            const res = await fetchWithAuth(url, {
                method,
                body: JSON.stringify(data)
            });

            if (res.ok) {
                showToast?.(editingJob ? "Anzeige aktualisiert!" : "Anzeige veröffentlicht!", "success");
                setShowPostJobModal(false);
                setEditingJob(null);
                fetchData();
            } else {
                const err = await res.json();
                const errorMsg = err.detail || (err.description ? `Beschreibung: ${err.description}` : "Fehler beim Speichern. Bitte alle Felder prüfen.");
                alert(errorMsg);
            }
        } catch (error) {
            alert("Verbindungsfehler.");
        }
    };

    const handleFeatureJob = async (jobId) => {
        setFeatureJobLoading(jobId);
        try {
            const res = await fetchWithAuth(`/api/billing/checkout-job/${jobId}/`, { method: 'POST' });
            if (res.ok) {
                const data = await res.json();
                window.location.href = data.url;
            } else {
                const err = await res.json().catch(() => ({}));
                showToast?.(err.error || 'Fehler beim Erstellen der Checkout-Session.', 'error');
            }
        } catch (err) {
            console.error(err);
            showToast?.('Verbindungsfehler.', 'error');
        } finally {
            setFeatureJobLoading(null);
        }
    };

    const handleSelectTier = async (level) => {
        setPricingLoading(true);
        try {
            const res = await fetchWithAuth('/api/billing/checkout-subscription/', {
                method: 'POST',
                body: JSON.stringify({ level })
            });
            if (res.ok) {
                const data = await res.json();
                window.location.href = data.url;
            } else {
                const err = await res.json().catch(() => ({}));
                showToast?.(err.error || 'Fehler beim Erstellen der Checkout-Session.', 'error');
                setPricingLoading(false);
            }
        } catch (err) {
            console.error(err);
            showToast?.('Verbindungsfehler.', 'error');
            setPricingLoading(false);
        }
    };

    const handleUpdateCareerSettings = async (updates) => {
        setCareerSettingsLoading(true);
        try {
            const res = await fetchWithAuth('/api/career/settings/', {
                method: 'PATCH',
                body: JSON.stringify(updates)
            });
            if (res.ok) {
                const updated = await res.json();
                setCareerSettings(updated);
                showToast?.('Karriere-Einstellungen gespeichert!', 'success');
            }
        } catch (err) {
            console.error(err);
        } finally {
            setCareerSettingsLoading(false);
        }
    };

    const filteredJobs = jobs.filter(job => {
        const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                             job.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             job.sponsor_name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = selectedCategory === 'ALL' || job.category === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    return (
        <div className="container mx-auto px-4 py-8 max-w-7xl">
            {/* Hero Section */}
            <div className="relative mb-12 rounded-3xl overflow-hidden bg-gradient-to-br from-plantlix-900 via-plantlix-800 to-plantlix-700 p-8 md:p-12 text-white shadow-2xl shadow-black/50">
                <div className="relative z-10 flex flex-col md:flex-row items-center gap-8">
                    <div className="flex-1 max-w-2xl">
                        <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 backdrop-blur-md rounded-full text-[10px] font-black uppercase tracking-widest mb-6 border border-white/10">
                            ✨ Exklusiv für Plantlix-Nutzer
                        </div>
                        <h1 className="text-4xl md:text-6xl font-black mb-6 tracking-tight leading-tight">
                            Dein Karriere-Turbo <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-plantlix-300 to-emerald-100">im GaLaBau 🌿</span>
                        </h1>
                        <p className="text-lg md:text-xl font-medium text-white/80 mb-10 leading-relaxed">
                            Vernetze dich direkt mit den Top-Betrieben der Branche. <br className="hidden md:block" />
                            Entdecke handverlesene Jobangebote, die exakt zu deinem Skill-Level passen.
                        </p>
                        
                        {/* Search Bar */}
                        <div className="relative max-w-xl group">
                            <input
                                type="text"
                                placeholder="Stelle, Stadt oder Betrieb suchen..."
                                className="w-full bg-white/10 backdrop-blur-2xl border border-white/20 rounded-2xl py-5 pl-14 pr-4 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-plantlix-400 transition-all font-bold shadow-2xl group-hover:bg-white/15"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <IconSearch className="absolute left-5 top-1/2 -translate-y-1/2 w-6 h-6 text-white/50 group-hover:text-white/80 transition-colors" />
                        </div>
                    </div>

                    {/* Employer UI */}
                    <div className="w-full md:w-auto flex flex-col items-center justify-center p-6 bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl shadow-2xl">
                        {userProfile?.is_authorized_employer ? (
                            <div className="text-center">
                                {/* Level Badge */}
                                <div className="flex items-center justify-center gap-2 mb-3">
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white text-plantlix-600 rounded-full text-[10px] font-black uppercase">
                                        <IconCheck className="w-3 h-3" /> Verifiziert
                                    </span>
                                    {mySponsor?.level && (
                                        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] font-black uppercase ${
                                            mySponsor.level === 'GOLD' ? 'bg-yellow-400 text-yellow-900' :
                                            mySponsor.level === 'SILVER' ? 'bg-slate-300 text-slate-800' :
                                            'bg-white/20 text-white/80'
                                        }`}>
                                            {mySponsor.level === 'GOLD' ? '🥇' : mySponsor.level === 'SILVER' ? '🥈' : '🆓'} {mySponsor.level === 'BASIC' ? 'Basis' : mySponsor.level === 'SILVER' ? 'Silber' : 'Gold'}
                                        </span>
                                    )}
                                </div>
                                <h3 className="text-xl font-black mb-1">Betriebsportal</h3>
                                <p className="text-xs text-white/70 mb-4">{mySponsor?.name || 'Manager-Modus aktiv'}</p>
                                <div className="flex flex-col gap-3">
                                    <button 
                                        onClick={() => setShowPostJobModal(true)}
                                        className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-plantlix-600 hover:bg-plantlix-50 rounded-xl text-sm font-black transition-all shadow-lg"
                                    >
                                        <IconPlus className="w-4 h-4" /> Anzeige schalten
                                    </button>
                                    {/* Upgrade CTA – only show if not Gold */}
                                    {mySponsor?.level !== 'GOLD' && (
                                        <button 
                                            onClick={() => setShowPricingModal(true)}
                                            className="inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-gradient-to-r from-yellow-400 to-amber-500 text-white rounded-xl text-xs font-black transition-all shadow-lg shadow-amber-500/30 hover:shadow-amber-500/50 hover:scale-[1.02] active:scale-95"
                                        >
                                            ⭐ Sichtbarkeit upgraden
                                        </button>
                                    )}
                                    <button 
                                        onClick={() => setShowSponsorModal(true)}
                                        className="text-sm font-bold text-white/60 hover:text-white/90 transition-colors"
                                    >
                                        Profil verwalten
                                    </button>
                                </div>
                            </div>
                        ) : userProfile?.employer_status === 'REQUESTED' ? (
                            <div className="text-center py-4">
                                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                                    <IconCalendar className="w-6 h-6" />
                                </div>
                                <h3 className="text-lg font-bold mb-2">Antrag läuft</h3>
                                <p className="text-xs text-white/70 max-w-[200px]">Wir prüfen deinen Antrag. Du wirst benachrichtigt!</p>
                            </div>
                        ) : ['AZUBI', 'FACHARBEITER', 'SCHUELER'].includes(userProfile?.role) ? (
                            <div className="text-center">
                                <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-white/20">
                                    <IconFileText className="w-6 h-6" />
                                </div>
                                <h3 className="text-xl font-black mb-1">Digitaler Lebenslauf</h3>
                                <p className="text-xs text-white/70 mb-6 font-bold">Stelle dich Betrieben professionell vor.</p>
                                <button 
                                    onClick={() => setShowResumeModal(true)}
                                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-plantlix-600 hover:bg-plantlix-50 rounded-xl text-sm font-black transition-all shadow-lg"
                                >
                                    <IconEdit className="w-4 h-4" /> Lebenslauf bearbeiten
                                </button>
                            </div>
                        ) : (
                            <div className="text-center">
                                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <IconBriefcase className="w-6 h-6" />
                                </div>
                                <h3 className="text-lg font-bold mb-2">Betriebsinhaber?</h3>
                                <p className="text-xs text-white/70 mb-6 max-w-[200px]">Schalte Anzeigen für Jobs & Ausbildung direkt auf Plantlix.</p>
                                <button 
                                    onClick={() => setShowApplyModal(true)}
                                    className="w-full px-6 py-3 bg-white text-plantlix-600 hover:bg-plantlix-50 rounded-xl text-sm font-black transition-all shadow-lg"
                                >
                                    Arbeitgeber werden
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                {/* Decorative Element */}
                <div className="absolute top-0 right-0 w-1/2 h-full opacity-10 pointer-events-none transform translate-x-1/4 scale-150">
                    <LeafIcon className="w-full h-full" />
                </div>
            </div>

            {/* Candidate Dashboard Section */}
            {['AZUBI', 'FACHARBEITER', 'SCHUELER'].includes(userProfile?.role) && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                    {/* My Applications */}
                    <div className="md:col-span-2 bg-white dark:bg-white/5 rounded-3xl p-8 border border-gray-100 dark:border-white/5 shadow-sm">
                        <h3 className="text-xl font-black mb-6 flex items-center gap-2">
                            <IconBriefcase className="w-5 h-5 text-plantlix-500" /> Meine Bewerbungen
                        </h3>
                        {myApplications.length > 0 ? (
                            <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                                {myApplications.map(app => (
                                    <div key={app.id} className="p-4 bg-gray-50 dark:bg-white/5 rounded-2xl flex items-center justify-between group">
                                        <div>
                                            <h4 className="font-bold text-sm">{app.job_title}</h4>
                                            <p className="text-xs text-plantlix-600 font-medium">{app.sponsor_name}</p>
                                            <p className="text-[10px] text-gray-400 mt-1 uppercase font-black tracking-wider">
                                                Status: <span className={
                                                    app.status === 'ACCEPTED' ? 'text-plantlix-500' :
                                                    app.status === 'REJECTED' ? 'text-red-500' :
                                                    'text-blue-500'
                                                }>{app.status_display}</span>
                                            </p>
                                        </div>
                                        <div className="text-[10px] text-gray-400 font-bold whitespace-nowrap">
                                            {new Date(app.created_at).toLocaleDateString()}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-12 bg-gray-50/50 dark:bg-black/20 rounded-2xl border-2 border-dashed border-gray-100 dark:border-white/5">
                                <p className="text-sm text-gray-400 font-bold">Noch keine Bewerbungen gesendet.</p>
                            </div>
                        )}
                    </div>

                    {/* Career Opt-In Settings (V2) */}
                    <div className="bg-gradient-to-br from-plantlix-600 to-plantlix-800 rounded-3xl p-8 border border-plantlix-500/20 text-white shadow-lg shadow-plantlix-900/20">
                        <h3 className="text-xl font-black mb-2 text-white">🌿 Talent-Pool</h3>
                        <p className="text-xs text-white/80 mb-6 font-medium">Werde von Betrieben gefunden, bevor du dich bewirbst.</p>

                        {/* Opt-In Toggle */}
                        <div className="space-y-4">
                            <label className="flex items-center justify-between p-3 bg-white/10 backdrop-blur-md rounded-2xl cursor-pointer group hover:bg-white/15 transition-colors">
                                <div>
                                    <p className="text-sm font-black">Im Talent-Pool sichtbar</p>
                                    <p className="text-[10px] text-white/60">Arbeitgeber können dich finden</p>
                                </div>
                                <div
                                    onClick={() => handleUpdateCareerSettings({ career_opt_in: !careerSettings.career_opt_in })}
                                    className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${
                                        careerSettings.career_opt_in ? 'bg-white' : 'bg-white/20'
                                    }`}
                                >
                                    <div className={`absolute top-1 w-4 h-4 rounded-full shadow transition-transform ${
                                        careerSettings.career_opt_in ? 'translate-x-7 bg-plantlix-600' : 'translate-x-1 bg-white'
                                    }`} />
                                </div>
                            </label>

                            <label className="flex items-center justify-between p-3 bg-white/10 backdrop-blur-md rounded-2xl cursor-pointer hover:bg-white/15 transition-colors">
                                <div>
                                    <p className="text-sm font-black">Skills anzeigen</p>
                                    <p className="text-[10px] text-white/60">Dein Pflanzenwissen wird sichtbar</p>
                                </div>
                                <div
                                    onClick={() => handleUpdateCareerSettings({ career_skills_public: !careerSettings.career_skills_public })}
                                    className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${
                                        careerSettings.career_skills_public ? 'bg-white' : 'bg-white/20'
                                    }`}
                                >
                                    <div className={`absolute top-1 w-4 h-4 rounded-full shadow transition-transform ${
                                        careerSettings.career_skills_public ? 'translate-x-7 bg-plantlix-600' : 'translate-x-1 bg-white'
                                    }`} />
                                </div>
                            </label>

                            <div>
                                <label className="block text-xs font-black text-white/60 mb-1.5 ml-1">Deine Region</label>
                                <input
                                    type="text"
                                    value={careerSettings.career_region || ''}
                                    onChange={(e) => setCareerSettings(s => ({ ...s, career_region: e.target.value }))}
                                    onBlur={(e) => handleUpdateCareerSettings({ career_region: e.target.value })}
                                    placeholder="z.B. Sachsen, Dresden"
                                    className="w-full px-3 py-2 text-sm bg-white/10 border border-white/20 rounded-xl focus:ring-2 focus:ring-white/50 outline-none font-bold text-white placeholder-white/40"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Azubi Documents Section */}
            {['AZUBI', 'FACHARBEITER', 'SCHUELER'].includes(userProfile?.role) && (
                <div className="mb-12">
                    {/* My Documents */}
                    <div className="bg-white dark:bg-white/5 rounded-3xl p-8 border border-gray-100 dark:border-white/5 shadow-sm">
                        <h3 className="text-xl font-black mb-6 flex items-center gap-2">
                            <IconFileText className="w-5 h-5 text-plantlix-500" /> Bewerbungsunterlagen
                        </h3>
                        
                        {/* Upload Form */}
                        <form onSubmit={handleUploadDocument} className="flex gap-2 mb-6">
                            <input 
                                name="label" 
                                required 
                                placeholder="Bezeichnung (z.B. Zeugnis)" 
                                className="flex-1 px-4 py-2 text-sm bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-plantlix-500 outline-none font-bold"
                            />
                            <div className="relative">
                                <input 
                                    type="file" 
                                    name="file" 
                                    required 
                                    accept=".pdf"
                                    className="absolute inset-0 opacity-0 cursor-pointer w-full"
                                    onChange={(e) => {
                                        if (e.target.files[0]) showToast?.(`Datei gewählt: ${e.target.files[0].name}`, "info");
                                    }}
                                />
                                <div className="px-4 py-2 bg-gray-100 dark:bg-white/10 rounded-xl text-sm font-bold flex items-center gap-2 pointer-events-none">
                                    <IconPlus className="w-4 h-4" /> PDF
                                </div>
                            </div>
                            <button type="submit" className="px-4 py-2 bg-plantlix-600 text-white rounded-xl text-sm font-black shadow-lg shadow-plantlix-600/20">
                                Upload
                            </button>
                        </form>

                        <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
                            {myDocuments.map(doc => (
                                <div key={doc.id} className="p-3 bg-gray-50 dark:bg-white/5 rounded-xl flex items-center justify-between group hover:bg-plantlix-50 dark:hover:bg-plantlix-500/10 transition-colors">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 bg-white dark:bg-black rounded-lg flex items-center justify-center border border-gray-100 dark:border-white/10">
                                            <IconFileText className="w-4 h-4 text-plantlix-500" />
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-xs">{doc.label}</h4>
                                            <p className="text-[10px] text-gray-400">PDF-Dokument</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <a href={doc.file} target="_blank" rel="noopener noreferrer" className="text-[10px] font-black text-plantlix-600 hover:underline">Vorschau</a>
                                        <button 
                                            onClick={() => handleDeleteDocument(doc.id)}
                                            className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-500/20 rounded-lg transition-colors"
                                        >
                                            <IconTrash className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                            {myDocuments.length === 0 && (
                                <p className="text-center py-8 text-xs text-gray-400 italic">Noch keine Unterlagen hinterlegt.</p>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                {/* Sidebar Filter */}
                <div className="lg:col-span-1 space-y-6">
                    <div>
                        <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                            <IconFilter className="w-4 h-4" /> Kategorien
                        </h3>
                        <div className="flex flex-col gap-2">
                            {categories.map(cat => (
                                <button
                                    key={cat.id}
                                    onClick={() => setSelectedCategory(cat.id)}
                                    className={`text-left px-4 py-3 rounded-xl font-bold transition-all ${
                                        selectedCategory === cat.id
                                        ? 'bg-plantlix-600 text-white shadow-md'
                                        : 'bg-white dark:bg-white/5 text-gray-600 dark:text-gray-400 hover:bg-plantlix-50 dark:hover:bg-plantlix-500/10 hover:text-plantlix-600'
                                    }`}
                                >
                                    {cat.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Sponsor Sidebar */}
                    <div className="bg-gray-50 dark:bg-white/5 rounded-2xl p-6 border border-gray-100 dark:border-white/5">
                        <h3 className="text-sm font-black text-gray-400 uppercase tracking-widest mb-4">
                            Premium Partner
                        </h3>
                        <div className="space-y-4">
                            {sponsors.filter(s => s.level !== 'BASIC').map(sponsor => (
                                <a 
                                    key={sponsor.id} 
                                    href={sponsor.website} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="block group"
                                >
                                    <div className="aspect-video bg-white dark:bg-black rounded-xl p-4 flex items-center justify-center border border-gray-200 dark:border-white/10 group-hover:border-plantlix-500 transition-all shadow-sm">
                                        {sponsor.logo ? (
                                            <img src={sponsor.logo} alt={sponsor.name} className="max-h-full max-w-full object-contain" />
                                        ) : (
                                            <span className="font-bold text-gray-400">{sponsor.name}</span>
                                        )}
                                    </div>
                                </a>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Main Content: Job List */}
                <div className="lg:col-span-3">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-black">
                            {loading ? 'Jobs werden geladen...' : `${filteredJobs.length} Offene Stellen`}
                        </h2>
                    </div>

                    {loading ? (
                        <div className="space-y-4">
                            {[1, 2, 3].map(i => (
                                <div key={i} className="bg-white dark:bg-white/5 h-32 rounded-2xl animate-pulse" />
                            ))}
                        </div>
                    ) : filteredJobs.length > 0 ? (
                        <div className="space-y-4">
                            {filteredJobs.map(job => (
                                <div 
                                    key={job.id}
                                    className={`group relative bg-white dark:bg-white/5 border rounded-2xl p-6 hover:shadow-xl transition-all ${
                                        job.is_featured
                                        ? 'border-yellow-400 dark:border-yellow-500/60 shadow-lg shadow-yellow-500/10 ring-1 ring-yellow-400/30'
                                        : 'border-gray-100 dark:border-white/5 hover:shadow-plantlix-500/5'
                                    }`}
                                >
                                    {/* Featured Badge */}
                                    {job.is_featured && (
                                        <div className="absolute -top-3 left-6 flex items-center gap-1.5 px-3 py-1 bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full text-[10px] font-black text-white uppercase tracking-wider shadow-md">
                                            ⭐ Featured
                                        </div>
                                    )}

                                    <div className="flex flex-col md:flex-row gap-6">
                                        {/* Sponsor Logo */}
                                        <div className={`w-16 h-16 md:w-20 md:h-20 bg-gray-50 dark:bg-black rounded-2xl p-2 flex items-center justify-center flex-shrink-0 border group-hover:scale-105 transition-transform ${
                                            job.is_featured ? 'border-yellow-300 dark:border-yellow-500/40' : 'border-gray-100 dark:border-white/10'
                                        }`}>
                                            {job.sponsor_logo ? (
                                                <img src={job.sponsor_logo} alt={job.sponsor_name} className="max-h-full max-w-full object-contain" />
                                            ) : (
                                                <IconUsers className="w-8 h-8 text-gray-300" />
                                            )}
                                        </div>

                                        <div className="flex-1 min-w-0">
                                            <div className="flex flex-wrap gap-2 mb-2">
                                                <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-plantlix-100 text-plantlix-700 dark:bg-plantlix-500/20 dark:text-plantlix-400">
                                                    {job.category_display}
                                                </span>
                                                <span className="flex items-center gap-1.5 text-xs font-bold text-gray-400">
                                                    <IconMapPin className="w-3 h-3" /> {job.location}
                                                </span>
                                                {/* Match Score Badge */}
                                                {job.match_score !== null && job.match_score !== undefined && (
                                                    <span className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${
                                                        job.match_score >= 70 ? 'bg-plantlix-100 text-plantlix-700 dark:bg-plantlix-500/20 dark:text-plantlix-400' :
                                                        job.match_score >= 40 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-400' :
                                                        'bg-gray-100 text-gray-500 dark:bg-white/10'
                                                    }`}>
                                                        🎯 {job.match_score}% Match
                                                    </span>
                                                )}
                                            </div>
                                            <h3 className="text-xl font-black text-gray-900 dark:text-white mb-1 truncate">
                                                {job.title}
                                            </h3>
                                            <p className="text-sm font-bold text-plantlix-600 mb-1">
                                                {job.sponsor_name}{job.sponsor_city ? ` · ${job.sponsor_city}` : ''}
                                            </p>
                                            {/* Salary + Start Date */}
                                            <div className="flex flex-wrap gap-3 mb-3">
                                                {job.salary_range && (
                                                    <span className="text-xs text-gray-500 dark:text-gray-400 font-bold">💰 {job.salary_range}</span>
                                                )}
                                                {job.start_date && (
                                                    <span className="text-xs text-gray-500 dark:text-gray-400 font-bold">📅 Start: {new Date(job.start_date).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}</span>
                                                )}
                                            </div>
                                            <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 md:line-clamp-1 mb-4">
                                                {job.description}
                                            </p>
                                            
                                            <div className="flex items-center justify-between">
                                                <span className="flex items-center gap-1.5 text-xs text-gray-400 italic">
                                                    <IconCalendar className="w-3 h-3" /> Veröffentlicht am {new Date(job.published_at).toLocaleDateString()}
                                                </span>
                                                <div className="flex gap-2">
                                                    {/* Management Buttons (Owner/Admin) */}
                                                    {(userProfile?.is_authorized_employer && job.sponsor_name === mySponsor?.name) || user?.is_staff ? (
                                                        <div className="flex gap-2 flex-wrap">
                                                            {/* Feature Job Button / PayPal Checkout */}
                                                            {!job.is_featured && (
                                                                <div className="flex flex-col gap-2">
                                                                    {paypalJobId === job.id ? (
                                                                        <div className="min-w-[150px]">
                                                                            <PayPalCheckout 
                                                                                type="JOB"
                                                                                amount="49.00"
                                                                                itemId={job.id}
                                                                                onSuccess={() => {
                                                                                    setPaypalJobId(null);
                                                                                    fetchData();
                                                                                }}
                                                                            />
                                                                            <button 
                                                                                onClick={() => setPaypalJobId(null)}
                                                                                className="w-full text-[10px] font-bold text-gray-400 hover:text-gray-600 mt-1"
                                                                            >
                                                                                Abbrechen
                                                                            </button>
                                                                        </div>
                                                                    ) : (
                                                                        <button
                                                                            onClick={() => setPaypalJobId(job.id)}
                                                                            className="flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-yellow-400 to-amber-500 text-white rounded-xl text-xs font-black transition-all shadow-md shadow-amber-500/20 hover:shadow-amber-500/40 hover:scale-[1.02] active:scale-95"
                                                                            title="Anzeige als Featured hervorheben (49€ / 30 Tage)"
                                                                        >
                                                                            ⭐ Hervorheben
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            )}
                                                            {job.is_featured && (
                                                                <span className="flex items-center gap-1 px-3 py-2 bg-yellow-100 dark:bg-yellow-500/20 text-yellow-700 dark:text-yellow-400 rounded-xl text-xs font-black">
                                                                    ⭐ Aktiv bis {job.is_featured_until ? new Date(job.is_featured_until).toLocaleDateString('de-DE') : '–'}
                                                                </span>
                                                            )}
                                                            <button 
                                                                onClick={() => { setEditingJob(job); setShowPostJobModal(true); }}
                                                                className="p-2 bg-gray-100 hover:bg-gray-200 dark:bg-white/5 dark:hover:bg-white/10 rounded-xl transition-colors"
                                                                title="Bearbeiten"
                                                            >
                                                                <IconEdit className="w-4 h-4 text-plantlix-600" />
                                                            </button>
                                                            <button 
                                                                onClick={() => handleDeleteJob(job.id)}
                                                                className="p-2 bg-gray-100 hover:bg-gray-200 dark:bg-white/5 dark:hover:bg-white/10 rounded-xl transition-colors"
                                                                title="Löschen"
                                                            >
                                                                <IconTrash className="w-4 h-4 text-red-500" />
                                                            </button>
                                                        </div>
                                                    ) : null}

                                                    {/* Apply Button (Candidate) */}
                                                    {['AZUBI', 'FACHARBEITER', 'SCHUELER'].includes(userProfile?.role) && (
                                                        <>
                                                            {job.external_link ? (
                                                                <a 
                                                                    href={job.external_link} 
                                                                    target="_blank" 
                                                                    rel="noopener noreferrer"
                                                                    className="flex items-center gap-2 px-4 py-2 bg-plantlix-600 hover:bg-plantlix-500 text-white rounded-xl text-sm font-black transition-all shadow-lg shadow-plantlix-600/20"
                                                                >
                                                                    Extern Bewerben <IconChevronRight className="w-4 h-4" />
                                                                </a>
                                                            ) : (
                                                                <button 
                                                                    onClick={() => { setSelectedJob(job); setShowDirectApplyModal(true); }}
                                                                    className="flex items-center gap-2 px-6 py-2 bg-plantlix-600 hover:bg-plantlix-500 text-white rounded-xl text-sm font-black transition-all shadow-lg shadow-plantlix-600/20"
                                                                >
                                                                    Direkt Bewerben <IconChevronRight className="w-4 h-4" />
                                                                </button>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-20 bg-gray-50 dark:bg-white/5 rounded-3xl border-2 border-dashed border-gray-200 dark:border-white/10">
                            <IconSearch className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                            <h3 className="text-xl font-bold text-gray-500">Keine passenden Jobs gefunden</h3>
                            <p className="text-gray-400 mt-2">Versuche es mit einem anderen Suchbegriff oder einer anderen Kategorie.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Pricing Modal */}
            {showPricingModal && (
                <PricingModal
                    onClose={() => setShowPricingModal(false)}
                    onSelectTier={handleSelectTier}
                    currentLevel={mySponsor?.level || 'BASIC'}
                    isLoading={pricingLoading}
                />
            )}

            {/* Application Modal */}
            {showApplyModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-900 w-full max-w-lg rounded-3xl overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200">
                        <div className="p-8">
                            <h2 className="text-2xl font-black mb-2">Als Arbeitgeber bewerben</h2>
                            <p className="text-gray-500 mb-8">Erzähl uns kurz von dir und deinem Betrieb. Wir schalten dich dann schnellstmöglich frei.</p>
                            
                            {applicationStatus.success ? (
                                <div className="bg-plantlix-50 dark:bg-plantlix-500/10 p-6 rounded-2xl text-center border border-plantlix-100 dark:border-plantlix-500/20">
                                    <IconCheck className="w-12 h-12 text-plantlix-500 mx-auto mb-4" />
                                    <h3 className="text-lg font-bold text-plantlix-800 dark:text-plantlix-400 mb-2">Antrag gesendet!</h3>
                                    <p className="text-sm text-plantlix-700 dark:text-plantlix-500/80 mb-6">Wir haben deine Anfrage erhalten und prüfen diese nun.</p>
                                    <button 
                                        onClick={() => setShowApplyModal(false)}
                                        className="w-full py-3 bg-plantlix-600 text-white rounded-xl font-black"
                                    >
                                        Verstanden
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={handleApplyAsEmployer} className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-bold mb-1.5 ml-1">Betriebs-/Name</label>
                                        <input 
                                            name="company_name" 
                                            required 
                                            className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 focus:ring-2 focus:ring-plantlix-500 outline-none font-bold"
                                            placeholder="z.B. Musterbau GmbH"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold mb-1.5 ml-1">Website (optional)</label>
                                        <input 
                                            name="website" 
                                            type="url"
                                            className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 focus:ring-2 focus:ring-plantlix-500 outline-none font-bold"
                                            placeholder="https://..."
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold mb-1.5 ml-1">Deine Nachricht</label>
                                        <textarea 
                                            name="message" 
                                            rows="3"
                                            className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 focus:ring-2 focus:ring-plantlix-500 outline-none font-bold resize-none"
                                            placeholder="Warum möchtest du auf Plantlix werben?"
                                        ></textarea>
                                    </div>
                                    
                                    {applicationStatus.error && (
                                        <p className="text-red-500 text-xs font-bold">{applicationStatus.error}</p>
                                    )}

                                    <div className="flex gap-3 pt-4">
                                        <button 
                                            type="button" 
                                            onClick={() => { setShowApplyModal(false); setApplicationStatus({ loading: false, success: false, error: null }); }}
                                            className="flex-1 py-4 bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-400 rounded-xl font-bold"
                                        >
                                            Abbrechen
                                        </button>
                                        <button 
                                            type="submit"
                                            disabled={applicationStatus.loading}
                                            className="flex-1 py-4 bg-plantlix-600 text-white rounded-xl font-black shadow-lg shadow-plantlix-600/20 disabled:opacity-50"
                                        >
                                            {applicationStatus.loading ? 'Sende...' : 'Antrag senden'}
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            )}

             {/* Post Job Modal */}
            {showPostJobModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-900 w-full max-w-2xl rounded-3xl overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200">
                        <div className="p-8">
                            <div className="flex items-center justify-between mb-2">
                                <h2 className="text-2xl font-black">{editingJob ? 'Stellenanzeige bearbeiten' : 'Stellenanzeige schalten'}</h2>
                                <button onClick={() => { setShowPostJobModal(false); setEditingJob(null); }} className="text-gray-400 hover:text-gray-600">✕</button>
                            </div>
                            <p className="text-gray-500 mb-6 font-medium">
                                {editingJob ? 'Aktualisiere die Details deiner Anzeige.' : 'Inseriere eine neue Stelle für deinen Betrieb.'}
                            </p>
                            
                            <form onSubmit={handlePostJob} className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-bold mb-1.5 ml-1">Titel der Anzeige</label>
                                        <input 
                                            name="title" 
                                            defaultValue={editingJob?.title}
                                            required 
                                            className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 focus:ring-2 focus:ring-plantlix-500 outline-none font-bold" 
                                            placeholder="z.B. Azubi zum GaLaBauer (m/w/d)" 
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold mb-1.5 ml-1">Kategorie</label>
                                        <select 
                                            name="category" 
                                            defaultValue={editingJob?.category || 'APPRENTICE'}
                                            className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 focus:ring-2 focus:ring-plantlix-500 outline-none font-bold"
                                        >
                                            {categories.filter(c => c.id !== 'ALL').map(cat => (
                                                <option key={cat.id} value={cat.id}>{cat.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold mb-1.5 ml-1">Standort</label>
                                        <input 
                                            name="location" 
                                            defaultValue={editingJob?.location}
                                            required 
                                            className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 focus:ring-2 focus:ring-plantlix-500 outline-none font-bold" 
                                            placeholder="Stadt, Region" 
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold mb-1.5 ml-1">Beschreibung</label>
                                    <textarea 
                                        name="description" 
                                        defaultValue={editingJob?.description}
                                        rows="5" 
                                        required 
                                        className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 focus:ring-2 focus:ring-plantlix-500 outline-none font-bold resize-none" 
                                        placeholder="Was erwartet die Bewerber?"
                                    ></textarea>
                                </div>
                                
                                <div className="flex gap-3 pt-4">
                                    <button 
                                        type="button" 
                                        onClick={() => { setShowPostJobModal(false); setEditingJob(null); }} 
                                        className="flex-1 py-4 bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-400 rounded-xl font-bold"
                                    >
                                        Abbrechen
                                    </button>
                                    <button type="submit" className="flex-1 py-4 bg-plantlix-600 text-white rounded-xl font-black shadow-lg shadow-plantlix-600/20 hover:bg-plantlix-500 transition-colors">
                                        {editingJob ? 'Speichern' : 'Veröffentlichen'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Manage Sponsor Modal */}
            {showSponsorModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-900 w-full max-w-xl rounded-3xl overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200">
                        {!mySponsor ? (
                            <div className="p-12 text-center">
                                <div className="w-12 h-12 border-4 border-plantlix-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                                <p className="text-gray-500 font-bold">Lade Betriebsprofil...</p>
                            </div>
                        ) : (
                            <div className="p-8">
                                <div className="flex items-center justify-between mb-2">
                                    <h2 className="text-2xl font-black">Betriebsprofil verwalten</h2>
                                    <button onClick={() => setShowSponsorModal(false)} className="text-gray-400 hover:text-gray-600">✕</button>
                                </div>
                                <p className="text-gray-500 mb-8 font-medium">Diese Informationen werden auf deinen Stellenanzeigen angezeigt.</p>
                                
                                <form onSubmit={handleUpdateSponsor} className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-bold mb-1.5 ml-1">Name des Unternehmens</label>
                                        <input 
                                            name="name" 
                                            defaultValue={mySponsor.name} 
                                            required 
                                            className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 focus:ring-2 focus:ring-plantlix-500 outline-none font-bold" 
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-bold mb-1.5 ml-1">E-Mail für Anfragen</label>
                                            <input 
                                                name="email" 
                                                type="email"
                                                defaultValue={mySponsor.email} 
                                                className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 focus:ring-2 focus:ring-plantlix-500 outline-none font-bold" 
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-bold mb-1.5 ml-1">Telefon</label>
                                            <input 
                                                name="phone" 
                                                defaultValue={mySponsor.phone} 
                                                className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 focus:ring-2 focus:ring-plantlix-500 outline-none font-bold" 
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold mb-1.5 ml-1">Adresse</label>
                                        <input 
                                            name="address" 
                                            defaultValue={mySponsor.address} 
                                            className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 focus:ring-2 focus:ring-plantlix-500 outline-none font-bold" 
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-bold mb-1.5 ml-1">PLZ</label>
                                            <input 
                                                name="zip_code" 
                                                defaultValue={mySponsor.zip_code} 
                                                className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 focus:ring-2 focus:ring-plantlix-500 outline-none font-bold" 
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-bold mb-1.5 ml-1">Stadt</label>
                                            <input 
                                                name="city" 
                                                defaultValue={mySponsor.city} 
                                                className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 focus:ring-2 focus:ring-plantlix-500 outline-none font-bold" 
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold mb-1.5 ml-1">Website</label>
                                        <input 
                                            name="website" 
                                            type="url"
                                            defaultValue={mySponsor.website} 
                                            className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 focus:ring-2 focus:ring-plantlix-500 outline-none font-bold" 
                                            placeholder="https://..."
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold mb-1.5 ml-1">Kurzbeschreibung</label>
                                        <textarea 
                                            name="description" 
                                            defaultValue={mySponsor.description} 
                                            rows="3" 
                                            className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 focus:ring-2 focus:ring-plantlix-500 outline-none font-bold resize-none" 
                                        ></textarea>
                                    </div>
                                    
                                    <div className="flex gap-3 pt-6">
                                        <button 
                                            type="button" 
                                            onClick={() => setShowSponsorModal(false)} 
                                            className="flex-1 py-4 bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-400 rounded-xl font-bold"
                                        >
                                            Abbrechen
                                        </button>
                                        <button 
                                            type="submit" 
                                            className="flex-1 py-4 bg-plantlix-600 text-white rounded-xl font-black shadow-lg shadow-plantlix-600/20 hover:bg-plantlix-500 transition-colors"
                                        >
                                            Speichern
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Direct Application Modal (Candidate) */}
            <DirectApplyModal 
                isOpen={showDirectApplyModal}
                onClose={() => setShowDirectApplyModal(false)}
                job={selectedJob}
                resume={myResume}
                documents={myDocuments}
                status={applicationStatus}
                onApply={handleDirectApply}
            />

            {/* Resume Editor Modal (Candidate) */}
            <ResumeModal 
                isOpen={showResumeModal}
                onClose={() => setShowResumeModal(false)}
                initialData={myResume}
                onSave={handleUpdateResume}
            />
        </div>
    );
};

export default Career;
